/**
 * Application-wide configuration constants for Futyclub
 * This file contains all the key game mechanics configuration values
 */

/**
 * Grace period after market close (in minutes)
 * During this period, the market shows "Mercado cerrado" with no countdown
 */
export const MARKET_GRACE_PERIOD_MINUTES = 5;

/**
 * Interval of max number of players that Futyclub will put in the market
 */
export const MAX_PLAYERS_MARKET_MIN = 5;
export const MAX_PLAYERS_MARKET_MAX = 14;

/**
 * Points penalty applied per empty lineup slot when scoring a match week
 */
export const EMPTY_SLOT_PENALTY_POINTS = 4;

/**
 * Number of days a newly signed player is individually protected from release clause payments
 */
export const RELEASE_CLAUSE_INDIVIDUAL_PROTECTION_DAYS = 14;

/**
 * Public landing page where users can download the mobile apps.
 * Used when sharing an invitation to join a league.
 */
export const DOWNLOAD_URL = 'https://onelink.to/7gda8s';

/**
 * Fallback avatar shown when a user has no profile picture.
 */
export const DEFAULT_AVATAR_URL = 'https://ionicframework.com/docs/img/demos/avatar.svg';

/**
 * Maximum length of a league message, matching the LeagueMessage model
 * validation (`length: { maximum: 2000 }`).
 */
export const LEAGUE_MESSAGE_MAX_LENGTH = 2000;

/**
 * Number of channels fetched per page in the channel search screen.
 */
export const CHANNEL_SEARCH_PAGE_SIZE = 20;

/**
 * Minimum length of the channel search term before a query is fired. Mirrors the
 * backend safety net in `SearchChannelsResolver` (`MIN_TERM_LENGTH`).
 */
export const CHANNEL_SEARCH_MIN_TERM_LENGTH = 2;

/**
 * Minimum organization-search length. MySQL's full-text index ignores tokens
 * shorter than three characters with its default InnoDB configuration.
 */
export const CAREER_ORGANIZATION_SEARCH_MIN_TERM_LENGTH = 3;

/**
 * Number of public profiles fetched per page in the career-overlap screen.
 */
export const CAREER_OVERLAPS_PAGE_SIZE = 20;

/**
 * Number of rows fetched per page in the "Red" tab (profiles and organizations)
 * and in the career organization page.
 */
export const NETWORK_PAGE_SIZE = 20;

/**
 * Minimum search length in the "Red" tab. Both searches use MySQL full-text
 * indexes, which ignore tokens shorter than three characters.
 */
export const NETWORK_SEARCH_MIN_TERM_LENGTH = 3;

/**
 * Maximum length of the career status line shown on the profile (e.g.
 * "Jugador de Avilés Stadium"). Must match User::CAREER_STATUS_MAX_LENGTH
 * in the backend.
 */
export const CAREER_STATUS_MAX_LENGTH = 120;

/**
 * Maximum length of the optional details of a career entry (e.g. "Liga
 * Nacional"). Must match CareerEntry::DETAILS_MAX_LENGTH in the backend.
 */
export const CAREER_ENTRY_DETAILS_MAX_LENGTH = 80;

/**
 * Number of leagues fetched per page in the league search screen.
 */
export const LEAGUE_SEARCH_PAGE_SIZE = 20;

/**
 * Minimum length of the league search term before a query is fired. Mirrors the
 * backend minimum in `SearchFantasyLeaguesResolver`.
 */
export const LEAGUE_SEARCH_MIN_TERM_LENGTH = 3;

/**
 * How often the authenticated user account is refreshed from the backend.
 * This keeps locally managed permissions current without requiring a new login.
 */
export const USER_ACCOUNT_POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Maximum size (bytes) of an uploaded image (profile picture, league image, post
 * photo). Mirrors GameConstants::UPLOADED_IMAGE_MAX_BYTES on the backend, which
 * rejects anything bigger; the web picker re-encodes photos to stay under it.
 */
export const UPLOADED_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Maximum length of the name the user is known by ("Conocido como"). Must
 * match User::DISPLAY_NAME_MAX_LENGTH in the backend.
 */
export const DISPLAY_NAME_MAX_LENGTH = 60;
